/**
 * Sentry Error Tracking Utilities
 * 
 * Provides helper functions for manual error capturing and user context.
 */
import * as Sentry from "@sentry/react";

/**
 * Set user context for error tracking
 * Call this after user logs in
 */
export const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    // Don't include sensitive data
  });
};

/**
 * Clear user context
 * Call this on logout
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Capture a custom error with additional context
 */
export const captureError = (error, context = {}) => {
  Sentry.withScope((scope) => {
    // Add extra context
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    
    Sentry.captureException(error);
  });
};

/**
 * Capture a message (non-error) for tracking
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    
    Sentry.captureMessage(message);
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (message, category = 'action', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

/**
 * Set custom tag for filtering errors
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

/**
 * Start a performance transaction
 */
export const startTransaction = (name, op = 'task') => {
  return Sentry.startInactiveSpan({ name, op });
};

/**
 * Test Sentry integration by triggering a test error
 */
export const testSentryIntegration = () => {
  try {
    throw new Error('Sentry Test Error - Frontend');
  } catch (error) {
    Sentry.captureException(error);
    return true;
  }
};

export default {
  setSentryUser,
  clearSentryUser,
  captureError,
  captureMessage,
  addBreadcrumb,
  setTag,
  startTransaction,
  testSentryIntegration,
};
